import { dataTypes, dataClass } from '../common/Constants';
import PlaceData from './PlaceData';
import GeoLocation from './GeoLocation';
import Config from '../common/Config';
import GeoService from "../common/GeoService";
import MapData from "../common/MapData";
import VisibilityService from "../common/VisibilityService";

/** Item as returned by the POI list endpoint. */
export interface POIListItem {
    id: number | string;
    uri?: string;
    url?: string;
    field_poi_geopos?: string;
    field_poi_type_reference?: string;
    /** Siempre presente: fromListItem lo usa como indice sin comprobar. */
    field_poi_type_tid: string;
    field_poi_rating: { value: string };
    title?: string;
    type?: string;
}

/**
 * Object to store hotel data from rest list.
 *
 * Example list item.
 * {
    "id":"34268",
    "uri":"https://test.gronze.com/maps20/poi/34268",
    "url":"https://test.gronze.com/node/34268",
    "field_poi_rating": {value: "1"}
    "field_poi_geopos":"{\"type\":\"Point\",\"coordinates\":[-7.5605461,43.0100889]}",
    "field_poi_type_reference":"monumento",
    "field_poi_type_tid":"6334",
    "title":"Porta Miñá",
    "type":"poi"},
   }
*/

class POIData extends PlaceData {

    /*
    field_poi_type_tid tipe conversion table
     */
    static POITypes: Record<string, string> = {
        '6335': 'templo',
        '6334': 'monumento',
        '6336': 'cultura-generico',
        '6332': 'bifurcacion',
        '6333': 'desvio',
        '6331': 'bicicletas',
        '6338': 'naturaleza',
        '6337': 'parque',
        '6408': 'fuente',
        '6339': 'playa',
        '6340': 'mirador',
        '6409': 'alto',
        '6410': 'cementerio',
        '6411': 'oficina-turismo',
        '6341': 'estructura',
        '6342': 'advertencia',
        '6343': 'informacion',
    }

    /** Icon name resolved from field_poi_type_tid. @see fromListItem */
    poiType?: string;

    /** POI rating, as sent by the backend. */
    rating?: string;

    /**
     * Returns POI[id]
     */
    getMapItemId(): string {
      return 'POI' + this.id;
    }

    /**
     * Devuelve el HTML del texto del POI (rating + nombre) para el marcador.
     */
    getPoiNameHtml(): string {
        let rating = '';
        if (this.poiType === POIData.POITypes['6335'] ||
            this.poiType === POIData.POITypes['6334'] ||
            this.poiType === POIData.POITypes['6336']
        ) {
            rating = POIData.createStars(this.rating);
        }
        return `<div class="poi-name">${rating}${this.name}</div>`;
    }

    /**
     * Create from data type, id
     */
    static fromListItem(item: POIListItem): POIData {
      const data = new POIData(dataTypes.poi, item.id);
      data.restUrl = item.uri;
      data.linkUrl = item.url;
      if (item.field_poi_geopos) {
        data.location = GeoLocation.fromGeoJson(item.field_poi_geopos);
      }
      const iconclass = this.POITypes[item.field_poi_type_tid] ? this.POITypes[item.field_poi_type_tid] : 'not-found'
      data.iconClass = 'gronze-icon-' + iconclass;
      data.name = item.title;
      data.poiType = iconclass;
      data.type = dataTypes.poi;
      data.rating = item.field_poi_rating.value;
      return data;
    }

    /**
     * Create DOM Element
     */
    createElement(): HTMLElement {
        const el = super.createElement();
        // Detect if this item is currently selected by checking MapData, not the DOM
        const isSelected = MapData.POIList.getSelectedPlace()?.id === this.id;

        // Si por nivel de zoom no toca mostrar POIs, no dibujamos nada.
        if (!GeoService.showHotels(MapData.zoom)) {
            el.className = '';
            el.innerHTML = '';
            return el;
        }

        // POIs de tipo 6332 (bifurcacion), 6342 (advertencia) y 6333 (desvio)
        // siempre se muestran, independientemente del selector de visibilidad
        const alwaysVisibleTypes = [
            POIData.POITypes['6332'], // bifurcacion
            POIData.POITypes['6342'], // advertencia
            POIData.POITypes['6333']  // desvio
        ];
        const isAlwaysVisible = this.poiType !== undefined
            && alwaysVisibleTypes.includes(this.poiType);

        const showPOIs = VisibilityService.getShowPOIs(); // '1' | '0'
        const zoom = MapData.zoom ?? 0;
        const zoomMarkersAlwaysShown = Config.config().zoom_level_markers_always_shown ?? 18;
        const forceShowByZoom = zoom >= zoomMarkersAlwaysShown;

        if (showPOIs === '1' || isAlwaysVisible) {
            el.className = dataClass.poi + ' icon glyphicons ' + this.iconClass;
        } else {
            // Mantenemos la clase del icono para aplicar el color de fondo correspondiente
            el.className = dataClass.poi + ' iconHide ' + this.iconClass;
            if (forceShowByZoom) {
                el.classList.add('marker-always-shown');
                el.classList.add('icon', 'glyphicons');
                if (GeoService.showHotelNames(MapData.zoom)) {
                    el.innerHTML = this.getPoiNameHtml();
                }
            } else {
                el.innerHTML = '';
            }
            if (isSelected) el.classList.add('selected');
            return el;
        }
        if (GeoService.showHotelNames(MapData.zoom)) {
            el.innerHTML = this.getPoiNameHtml();
        }
        if (isSelected) el.classList.add('selected');
        return el;
     }

    static haveRating(poiTypeReference?: string): boolean {
        return poiTypeReference === '6335'||
            poiTypeReference === '6334' ||
            poiTypeReference === '6336';
    }

    static createStars(rating?: string): string {
        let stars = '';
        for (let i = 1; i < 4; i++) {
            // rating llega como cadena del backend. La comparacion i > rating ya
            // hacia esta conversion de forma implicita en JavaScript.
            if (i > Number(rating)) {
                stars += '<span class="star off"></span>';
            } else {
                stars += '<span class="star on"></span>';
            }
        }
        return `<div class="rating">${stars}</div>`;
    }
}

export default POIData;
